import React from 'react';
import { EntityChip } from '../components/redesign/EntityChip';
import { Button, Card } from '../components/redesign/ui';
import {
  Plus, ArrowsClockwise, PencilSimple, Trash, EnvelopeSimple,
  FlowArrow, WarningCircle,
} from '@phosphor-icons/react';

// TypeBadge — three fixed variants matching the RECIPIENT_TYPES vocabulary.
// Tones preserved from live: observer=blue, weekly=amber, monthly=red.
const TYPE_META = {
  observer:       { label: 'Observer',       cls: 'bg-status-under-process/12 text-status-under-process' },
  weekly_report:  { label: 'Weekly Report',  cls: 'bg-status-pending-finance/12 text-status-pending-finance' },
  monthly_report: { label: 'Monthly Report', cls: 'bg-status-escalated/12 text-status-escalated' },
};

function TypeBadge({ typeKey }) {
  const meta = TYPE_META[typeKey];
  if (!meta) return null;
  return (
    <span className={`inline-flex rounded-chip px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="border-b border-paper-border">
      {[1, 2, 3, 4, 5].map((i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 animate-pulse rounded bg-paper-muted" style={{ width: `${60 + i * 10}%` }} />
        </td>
      ))}
    </tr>
  );
}

function Th({ children, extra = '' }) {
  return (
    <th className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-ink-faint ${extra}`}>{children}</th>
  );
}

// Coverage-alert banner (amber, no trailing link).
function CoverageBanner({ alert }) {
  if (!alert) return null;
  return (
    <div className="flex items-start gap-3 rounded-card border border-status-pending-finance/30 bg-status-pending-finance/8 p-3 text-sm text-status-pending-finance">
      <WarningCircle size={18} className="mt-0.5 flex-none" />
      <span>{alert.message}</span>
    </div>
  );
}

// Restricted view — shown when a non-super_admin lands here (defense in depth;
// route-level ProtectedRoute already gates the page).
function RestrictedCard() {
  return (
    <div className="p-1">
      <Card className="p-8 text-center">
        <div className="mb-2 font-serif text-base text-ink">Access restricted</div>
        <p className="text-sm text-ink-muted">This page is available to super admin only.</p>
      </Card>
    </div>
  );
}

export default function FlowsView({
  user,
  rows = [],
  flows = [],
  loading = false,
  typeSummary = '',
  coverageAlert = null,
  onRefresh,
  onAddRecipient,
  onEditRecipient,
  onRemoveRecipient,
}) {
  if (user?.role !== 'super_admin') return <RestrictedCard />;

  return (
    <div className="p-1">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2.5 font-serif text-2xl text-ink">
              <FlowArrow size={24} weight="regular" className="text-status-under-process" />
              Flow Notifications
            </h1>
            <p className="mt-1 text-[13px] text-ink-muted">
              {typeSummary || 'Manage who receives notifications for each complaint flow.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              title="Refresh"
              className="inline-flex h-9 w-9 items-center justify-center rounded-field border border-paper-border2 text-ink-muted hover:bg-paper-muted">
              <ArrowsClockwise size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <Button variant="primary" onClick={onAddRecipient}>
              <Plus size={14} weight="bold" />Add Recipient
            </Button>
          </div>
        </div>

        {/* Coverage alert (only when observers are missing for at least one entity) */}
        <CoverageBanner alert={coverageAlert} />

        {/* Recipients table */}
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-paper-border bg-paper-muted/40">
                  <Th extra="w-56">Recipient</Th>
                  <Th extra="w-40">Entities</Th>
                  <Th>Flows</Th>
                  <Th extra="w-52">Types</Th>
                  <Th extra="w-24 !text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /></>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <FlowArrow size={40} className="text-ink-faint opacity-40" />
                        <p className="text-sm font-medium text-ink-muted">No notification recipients yet.</p>
                        <p className="text-xs text-ink-faint">Add your first recipient using the button above.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.email} className="border-b border-paper-border transition-colors hover:bg-paper-muted/50">
                      {/* Recipient */}
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-ink" dir="auto">
                          {row.label || <span className="text-xs italic text-ink-faint">No label</span>}
                        </div>
                        <div className="mt-0.5 flex items-center gap-1 text-xs text-ink-faint">
                          <EnvelopeSimple size={12} className="flex-none" />{row.email}
                        </div>
                      </td>
                      {/* Entities */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.entities.map((slug) => (
                            <EntityChip key={slug} entitySlug={slug} />
                          ))}
                        </div>
                      </td>
                      {/* Flows */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.flows.map((f) => (
                            <span key={f.id}
                              className="inline-flex rounded-chip bg-paper-muted px-2 py-0.5 text-[11px] font-medium text-ink-muted">
                              {f.display_name}
                            </span>
                          ))}
                        </div>
                      </td>
                      {/* Types */}
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {row.types.map((t) => <TypeBadge key={t} typeKey={t} />)}
                        </div>
                      </td>
                      {/* Actions */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => onEditRecipient(row.email)}
                            title="Edit"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-under-process">
                            <PencilSimple size={14} />
                          </button>
                          <button
                            onClick={() => onRemoveRecipient(row.email)}
                            title="Remove"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-field text-ink-faint transition hover:bg-paper-muted hover:text-status-escalated">
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer count */}
          {!loading && rows.length > 0 && (
            <div className="border-t border-paper-border bg-paper-muted/40 px-4 py-2.5 text-xs text-ink-faint">
              {rows.length} recipient{rows.length > 1 ? 's' : ''} across {flows.length} flow{flows.length === 1 ? '' : 's'}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
